import { useSelector } from 'react-redux';
import { ReduxState, useAppDispatch } from '../../../../../store';
import { useCallback, useMemo } from 'react';
import {
  setImportItems,
  setImportStep,
  setImportVerificationTimeStamp,
} from '../../../../Calculations/calculationsSlice';
import {
  InstructionType,
  ProcessingStep,
  RedistributionImportInputData,
} from './redistributionImportTypes';
import {
  apiCalculationRedistributionImport,
  useCreateCalculationRedistributionImportMutation,
  usePutCalculationRedistributionImportItemsMutation,
} from './apiCalculationRedistributionImport';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { ApiOperationType } from '@ydistri/api-sdk';
import { addToast } from '@ydistri/utils';
import { useTemplateOrCalculation } from '../../../../../hooks/useTemplateOrCalculation';
import { getRequestErrorMessage } from '../../../../../lib/utils/getRequestErrorMessage';
import useRedistributionImportFormat from './useRedistributionImportFormat';
import { createRedistributionImportValidators } from './redistributionImportValidatorsLib';
import {
  getPlainTextFromStructuredData,
  getValidRows,
  selectImportableItemParser,
} from '../../../../../lib/imports/importLib';

const convertToBase64 = (data: string): string => {
  const textEncoder = new TextEncoder();
  const encodedData = textEncoder.encode(data);

  const binString = Array.from(encodedData, byte => String.fromCodePoint(byte)).join('');
  return btoa(binString);
};

export type ImportItemFunction = () => void;

export interface UseImportItemsData {
  isPending: boolean;
  callback: ImportItemFunction;
}

/**
 * Construct header of CSV file based on an instruction type
 * @param instructionType
 */
const getCSVHeader = (instructionType: InstructionType) => {
  const headers = [
    'ProductIdentification',
    'SourceStoreIdentification',
    'TargetStoreIdentification',
  ];

  //Quantity column comes before Description column
  if (
    instructionType === InstructionType.QUANTITY ||
    instructionType === InstructionType.QUANTITY_DESCRIPTION
  ) {
    headers.push('Quantity');
  }

  if (
    instructionType === InstructionType.DESCRIPTION ||
    instructionType === InstructionType.QUANTITY_DESCRIPTION
  ) {
    headers.push('Description');
  }

  return headers.join(',');
};

/**
 * Hook provides a function to be used to pick up the pasted content, parse it, validate
 * what can be validated locally (duplicate rows, invalid quantity) and
 * submit valid data to backend for further validation and processing.
 */
const useImportRedistributionItems = (): UseImportItemsData => {
  const dispatch = useAppDispatch();
  const identificationData = useSelector(
    (state: ReduxState) => state.calculations.redistributionImport.identificationData,
  );
  const instructionSpec = useSelector(
    (state: ReduxState) => state.calculations.redistributionImport.instructionSpecification,
  );
  const importRawContent = useSelector(
    (state: ReduxState) => state.calculations.redistributionImport.inputContent,
  );

  const calculation = useTemplateOrCalculation();

  const [createCalculationRedistributionImport, createCalculationRedistributionImportStatus] =
    useCreateCalculationRedistributionImportMutation();

  const [putCalculationRedistributionImportItems, putCalculationRedistributionImportItemsStatus] =
    usePutCalculationRedistributionImportItemsMutation();

  const inputFormat = useRedistributionImportFormat();

  //handle Error from importing items - display a message to the user and return process to the start
  const handleError = useCallback(
    (err: unknown) => {
      dispatch(
        addToast({
          message: getRequestErrorMessage(err, 'Import failed. Please try again.'),
          isError: true,
        }),
      );
      dispatch(setImportStep({ stepIndex: ProcessingStep.IMPORT, stepRunning: false }));
    },
    [dispatch],
  );

  const handleItemsError = useCallback(
    async (error: unknown) => {
      // Upload may have staged the rows before snapshot validation rejected them.
      const summary = await dispatch(
        apiCalculationRedistributionImport.endpoints.getCalculationRecordImportSummary.initiate(
          calculation.id,
          { forceRefetch: true, subscribe: false },
        ),
      );
      if (summary.isError || summary.data?.some(item => item.itemCount > 0)) {
        if (!summary.isError) {
          dispatch(
            addToast({
              message: getRequestErrorMessage(
                error,
                'Upload failed. Review the staged items before continuing.',
              ),
              isError: true,
            }),
          );
        }
        dispatch(setImportStep({ stepIndex: ProcessingStep.VALIDATION, stepRunning: false }));
      } else {
        handleError(error);
      }
    },
    [calculation.id, dispatch, handleError],
  );

  // Main callback to be returned to the user
  const callback = useCallback(() => {
    if (importRawContent.length === 0) {
      return;
    }

    const parser = selectImportableItemParser<RedistributionImportInputData>(['text/plain']);
    if (!parser) {
      return;
    }

    const validators = createRedistributionImportValidators(instructionSpec, inputFormat);

    const parsedRows = parser.parse(importRawContent, inputFormat, validators);
    if (!(parsedRows.length > 0)) {
      return;
    }

    const validRows = getValidRows(parsedRows);
    if (validRows.length === 0) {
      dispatch(
        addToast({
          message: 'No valid rows found in the import data',
          isError: true,
        }),
      );
      return;
    }

    dispatch(setImportStep({ stepIndex: ProcessingStep.VALIDATION, stepRunning: true }));
    dispatch(setImportItems(parsedRows));

    const verificationTimeStamp = new Date().toISOString();

    const productIdentificationData = identificationData.find(
      identification => identification.type === 'Product',
    );
    const storeIdentificationData = identificationData.find(
      identification => identification.type === 'Store',
    );

    //prepare data for backend
    const validRowsText = getPlainTextFromStructuredData(validRows, inputFormat, ',');
    const header = getCSVHeader(instructionSpec);

    const itemsWithHeader = `${header}\n${validRowsText}`;
    const encodedItemsWithHeader = convertToBase64(itemsWithHeader);

    if (productIdentificationData && storeIdentificationData) {
      createCalculationRedistributionImport({
        calculationId: calculation.id,
        verificationTimeStamp,
        apiOperationType: ApiOperationType.Create,
        productIdentificationTypeId: productIdentificationData.value,
        storeIdentificationTypeId: storeIdentificationData.value,
      })
        .unwrap()
        .then(() => {
          dispatch(setImportVerificationTimeStamp(verificationTimeStamp));

          putCalculationRedistributionImportItems({
            calculationId: calculation.id,
            importItemsEncodedBase64: encodedItemsWithHeader,
            verificationTimeStamp,
          })
            .unwrap()
            .then(() => {
              dispatch(setImportStep({ stepIndex: ProcessingStep.VALIDATION, stepRunning: false }));
            })
            .catch(handleItemsError);
        })
        .catch(handleError);
    } else {
      dispatch(
        addToast({
          message: 'Product and Store identification must be specified',
          isError: true,
        }),
      );
      return;
    }
  }, [
    calculation.id,
    createCalculationRedistributionImport,
    dispatch,
    handleError,
    handleItemsError,
    identificationData,
    importRawContent,
    inputFormat,
    instructionSpec,
    putCalculationRedistributionImportItems,
  ]);

  const isPending =
    createCalculationRedistributionImportStatus.status === QueryStatus.pending ||
    putCalculationRedistributionImportItemsStatus.status === QueryStatus.pending;

  return useMemo(() => ({ callback, isPending }), [callback, isPending]);
};

export default useImportRedistributionItems;
